import { Component, input, model, output } from "@angular/core";
import { ButtonModule } from "primeng/button";
import { AssertionIssue, Media } from "../../../../../../core/database/db";
import { PhotoCaptureButtonComponent } from "../../../../../../shared/components/photo-capture-button/photo-capture-button.component";
import { SectionHeaderComponent } from "../../../../../../shared/components/section-header/section-header.component";
import { StandardBottomSheetComponent } from "../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { AssertionImageGalleryComponent } from "../../../../../media/components/assertion-image-gallery/assertion-image-gallery.component";

/** One logged issue plus the photos attached to it. */
export interface ChecklistLoggedIssue {
  issue: AssertionIssue;
  media: Media[];
}

export interface ChecklistIssuePhotos {
  issue: AssertionIssue;
  files: File[];
}

/**
 * The issues logged against a single Needs Attention checklist item.
 *
 * An item can be flagged more than once, so this lists every open issue and lets the CM add a photo
 * straight from the card. Editing one opens the issue sheet, which is where its notes and deletion
 * are handled.
 */
@Component({
  selector: "app-checklist-issues-sheet",
  standalone: true,
  imports: [
    ButtonModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    VerticalButtonGroupComponent,
    PhotoCaptureButtonComponent,
    AssertionImageGalleryComponent
  ],
  templateUrl: "./checklist-issues-sheet.component.html",
  styleUrl: "./checklist-issues-sheet.component.scss"
})
export class ChecklistIssuesSheetComponent {
  isOpen = model<boolean>(false);
  /** Checklist item the issues belong to; rendered as the sheet's context line (may contain markup). */
  assertionText = input<string>("");
  issues = input<ChecklistLoggedIssue[]>([]);
  /** Blocks the row actions while a save or delete is in flight. */
  busy = input<boolean>(false);

  readonly editRequested = output<AssertionIssue>();
  readonly photosSelected = output<ChecklistIssuePhotos>();
  readonly addIssueRequested = output<void>();
  readonly closed = output<void>();

  protected get footerButtons(): VerticalButton[] {
    return [
      {
        label: "Add Another Issue",
        severity: "contrast",
        disabled: this.busy(),
        styleClass: "bqa-primary-btn",
        action: () => this.addIssueRequested.emit(),
        testId: "checklist-issues-sheet-btn-add"
      },
      {
        label: "Close",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.close(),
        testId: "checklist-issues-sheet-btn-close"
      }
    ];
  }

  protected onSheetOpenChange(isOpen: boolean): void {
    if (!isOpen && this.isOpen()) {
      this.close();
    }
  }

  protected onPhotosSelected(issue: AssertionIssue, files: File[]): void {
    this.photosSelected.emit({ issue, files });
  }

  private close(): void {
    this.isOpen.set(false);
    this.closed.emit();
  }
}
